import { NestedHome, NestedProject, MapProject } from '@habity/types';
import Map, {
  FullscreenControl,
  GeolocateControl,
  NavigationControl,
  MapRef,
  MapEvent,
} from 'react-map-gl';
import { Box, Hide } from '@chakra-ui/react';
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MapCard } from '../ui/cards/MapCard';
import { useUser } from '~/hooks/useUser';
import { MAPBOX_CONFIG } from '~/constants/mapbox';
import { GeoJSONSource } from 'mapbox-gl';
import { fitPolygonBounds, resetMapBounds } from '~/lib/map';
import { useUIStore } from '~/store';
import { getAPI } from '~/lib/api';
import {
  AreaSourcesAndLayers,
  HomePointsSourcesAndLayers,
  ProjectPointsSourcesAndLayers,
  HOME_LAYER_ID,
  HOME_CLUSTER_ID,
  PROJECT_LAYER_ID,
  PROJECT_CLUSTER_ID,
} from './Sources';
import type { MapHome, MapHomeLocation } from '@habity/types';
import { ProjectMapCard } from '../ui/cards/ProjectMapCard';
import 'mapbox-gl/dist/mapbox-gl.css';
import { ZIndexLayers } from './LayerOrder';

const { ACCESS_TOKEN, CLUSTER_ZOOM_DURATION, MIN_ZOOM } = MAPBOX_CONFIG;
const mapStyleColor = 'mapbox://styles/mapbox/streets-v12';
const mapStyleGrayscale = 'mapbox://styles/mapbox/light-v11';

type ControlPosition =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-right'
  | 'top-left';

interface HomesMapProps {
  homes?: (MapHome | MapHomeLocation)[] | null;
  projects?: MapProject[] | null;
  height?: string;
  borderRadius?: string;
  polygons?: GeoJSON.Polygon[] | null;
  hidePolygonsLayer?: boolean;
  hideControls?: boolean;
  navLocation?: ControlPosition;
  grayscale?: boolean;
  clickable?: boolean;
}

const HomesMap = forwardRef<MapRef, HomesMapProps>(
  (
    {
      homes,
      projects,
      height = '100vh',
      borderRadius = 'auto',
      polygons,
      hidePolygonsLayer = false,
      hideControls = false,
      navLocation = 'bottom-right',
      grayscale = false,
      clickable = true,
    },
    ref: ForwardedRef<MapRef>
  ) => {
    const mapRef = useRef<MapRef | null>(null);
    const mapContainerRef = useRef<HTMLDivElement | null>(null);
    useImperativeHandle(ref, () => mapRef.current as MapRef);

    const [home, setHome] = useState<NestedHome | undefined>();
    const [project, setProject] = useState<NestedProject | undefined>();
    const { user } = useUser();
    const [homeLoading, setHomeLoading] = useState(false);
    const [projectLoading, setProjectLoading] = useState(false);

    const setMapViewState = useUIStore((state) => state.setMapViewState);
    const mapViewState = useUIStore((state) => state.mapViewState);

    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const isAnimating = useRef(false);
    const prevPolygonsRef = useRef<string>('');

    async function combinePolygonsAndFlyToBbox(polygons: GeoJSON.Polygon[]) {
      if (!isMapLoaded || !mapRef.current) return;
      isAnimating.current = true;
      fitPolygonBounds(mapRef, polygons, 50);
      // Reset after animation completes (default duration is 500ms)
      setTimeout(() => {
        isAnimating.current = false;
      }, 600);
    }

    // Zoom to bounds when map is loaded and polygons are available
    // Reset to default bounds when polygons are removed
    // Only fly when polygon content actually changes (not on every render)
    useEffect(() => {
      if (!isMapLoaded || !mapRef.current) return;

      // Create a stable key from polygon coordinates to detect actual changes
      const polygonsKey = polygons
        ? JSON.stringify(polygons.map((p) => p.coordinates))
        : '';

      // Skip if polygons haven't actually changed
      if (polygonsKey === prevPolygonsRef.current) return;
      prevPolygonsRef.current = polygonsKey;

      if (!polygons || polygons.length === 0) {
        isAnimating.current = true;
        resetMapBounds(mapRef);
        setTimeout(() => {
          isAnimating.current = false;
        }, 600);
      } else {
        combinePolygonsAndFlyToBbox(polygons);
      }
    }, [polygons, isMapLoaded]);

    const homeLayers = [HOME_LAYER_ID, HOME_CLUSTER_ID];
    const projectLayers = [PROJECT_LAYER_ID, PROJECT_CLUSTER_ID];
    const layers = projects ? projectLayers : homeLayers;

    const handleMapLoad = (e: MapEvent) => {
      const { target } = e;
      const map = target;

      map.on('mouseenter', layers, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', layers, () => {
        map.getCanvas().style.cursor = '';
      });
    };

    const onClick = useCallback(async (event: TODO) => {
      if (!mapRef.current) return;

      const { features } = event;
      const hoveredFeature = features && features[0];
      const { properties, source } = hoveredFeature || {};
      const { id, cluster, cluster_id } = properties || {};

      if (cluster && cluster_id) {
        const mapboxSource = mapRef.current.getSource(source) as GeoJSONSource;
        mapboxSource.getClusterExpansionZoom(cluster_id, (err, zoom) => {
          if (err) {
            return;
          }

          mapRef &&
            mapRef.current &&
            mapRef.current.easeTo({
              center: hoveredFeature.geometry.coordinates,
              zoom,
              duration: CLUSTER_ZOOM_DURATION,
            });
        });
      } else if (id) {
        // Determine if this is a project or home based on source
        if (source === 'projects-source') {
          setProjectLoading(true);
          setHome(undefined);
          const { data: projectData } = await getAPI(
            `/providers/neondatabase/projects/${id}`
          );
          if (projectData) setProject(projectData);
          setProjectLoading(false);
        } else {
          // Show home
          setHomeLoading(true);
          setProject(undefined);
          const { data: home } = await getAPI(
            `/providers/supabase/homes/${id}`
          );
          if (home) setHome(home);
          setHomeLoading(false);
        }
      } else {
        // Hide cards
        setHome(undefined);
        setProject(undefined);
      }
    }, []);

    return (
      <Box ref={mapContainerRef} className="map" pb={8}>
        <Map
          ref={mapRef}
          mapboxAccessToken={ACCESS_TOKEN}
          style={{
            width: '100%',
            height,
            fontFamily: 'inter',
            borderRadius,
          }}
          localFontFamily="inter"
          logoPosition="bottom-right"
          mapStyle={grayscale ? mapStyleGrayscale : mapStyleColor}
          onClick={clickable ? onClick : () => {}}
          minZoom={MIN_ZOOM}
          attributionControl={false}
          interactiveLayerIds={layers}
          scrollZoom={false}
          initialViewState={mapViewState}
          onMove={(evt) => {
            if (!isAnimating.current) {
              setMapViewState(evt.viewState);
            }
          }}
          onLoad={(e) => {
            handleMapLoad(e);
            setIsMapLoaded(true);
          }}
        >
          <ZIndexLayers />
          {polygons && !hidePolygonsLayer && (
            <AreaSourcesAndLayers polygons={polygons} />
          )}
          {homes && <HomePointsSourcesAndLayers homes={homes} />}
          {projects && <ProjectPointsSourcesAndLayers projects={projects} />}

          {(homeLoading ||
            (home?.address?.longitude && home?.address?.latitude)) && (
            // @ts-ignore
            <MapCard
              isLoading={homeLoading}
              home={home}
              user={user || undefined}
            />
          )}

          {(projectLoading || (project?.latitude && project?.longitude)) && (
            <ProjectMapCard isLoading={projectLoading} project={project} />
          )}

          {hideControls ? null : (
            <Hide below="tablet">
              <NavigationControl position={navLocation} />
              <FullscreenControl position={navLocation} />
              <GeolocateControl position={navLocation} />
            </Hide>
          )}
        </Map>
      </Box>
    );
  }
);

HomesMap.displayName = 'HomesMap';

export { HomesMap };
